import React, { forwardRef } from 'react';

const COLORS = {
  blue: '#0B3C5D',
  orange: '#FF7800',
  success: '#16A34A',
  error: '#DC2626',
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled,
  ...props
}, ref) => {
  const baseStyles =
    'inline-flex items-center justify-center rounded-2xl font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

  const sizes: Record<string, string> = {
    xs: 'h-7 px-2.5 text-xs',
    sm: 'h-9 px-3 text-xs',
    md: 'h-11 px-5 text-sm',
    lg: 'h-12 px-8 text-base',
  };

  const variants: Record<string, string> = {
    primary: 'text-white hover:opacity-90 shadow-sm',
    secondary: 'text-white hover:opacity-90 shadow-sm',
    outline: 'border border-slate-200 bg-transparent hover:bg-slate-50 text-slate-700',
    ghost: 'bg-transparent hover:bg-slate-100 text-slate-700',
    danger: 'text-white hover:opacity-90 shadow-sm',
    success: 'text-white hover:opacity-90 shadow-sm',
  };

  const style: React.CSSProperties = {};
  if (variant === 'primary') style.backgroundColor = COLORS.orange;
  if (variant === 'secondary') style.backgroundColor = COLORS.blue;
  if (variant === 'danger') style.backgroundColor = COLORS.error;
  if (variant === 'success') style.backgroundColor = COLORS.success;

  return (
    <button
      ref={ref}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      style={style}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
});

Button.displayName = 'Button';
